import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateApprovalHistory } from '../ApprovalHistory/useApprovalHistoryQuery';
import { invalidateRegEdSubmissions } from './useRegEdSubmissionsQuery';

/**
 * Supersedes the rejected round and raises a fresh one for the same approver, keeping the revision on
 * the blog instead of in a new document. Sets `Pending` and clears the cancelled date that rejection
 * left behind. Refuses a published blog.
 *
 * Save the revised content first — this snapshots the body as the round's reviewed content — then
 * repoint the RegEd submission and regenerate the PDF, which is what files.
 */
export const useRegEdBlogReviseMutation = ({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (blogPost: Api.IBlogPost) => void;
} = {}) => {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: ({
			blogPostId,
			impersonationContext,
		}: {
			blogPostId: string;
			impersonationContext?: Api.IImpersonationContext;
		}) =>
			userSession.webServiceHelper.callAsync<Api.IBlogPost>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `blog/${encodeURIComponent(blogPostId)}/revise`,
				}),
				'POST',
				null
			),
		onError,
		onSuccess: (blogPost, { blogPostId }) => {
			invalidateRegEdSubmissions(blogPostId);
			invalidateApprovalHistory({ resourceId: blogPostId, resourceType: 'blog' });
			onSuccess?.(blogPost);
		},
	});
};
